import React from "react";
import { Flame, Star, LogOut, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";

export default function TopBar({ variant = "child" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!user) return null;

  const handleLogout = () => { logout(); nav("/login"); };

  if (variant === "parent") {
    return (
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
          <Link to="/parent" className="flex items-center gap-2" data-testid="parent-home-link">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold font-display">TL</div>
            <span className="font-display font-bold text-lg text-slate-800">Three-Lane Quest</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-600">
            <Link to="/parent" data-testid="nav-dashboard">Dashboard</Link>
            <Link to="/parent/messages" data-testid="nav-messages">Messages</Link>
            <Link to="/shared" data-testid="nav-shared">Shared</Link>
            <Link to="/parent/settings" data-testid="nav-settings">Settings</Link>
          </nav>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline">{user.name}</span>
            <button onClick={handleLogout} data-testid="logout-btn" className="text-slate-500 hover:text-rose-600"><LogOut className="w-5 h-5"/></button>
          </div>
        </div>
      </header>
    );
  }

  // Child variant
  const xpToNext = 100;
  const xpPct = Math.min(100, (user.xp % xpToNext));
  return (
    <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b-4 border-sky-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
        <Link to="/child" className="flex items-center gap-2" data-testid="child-home-link">
          <div className="w-10 h-10 rounded-2xl bg-sky-400 flex items-center justify-center text-white font-bold font-display shadow-[0_3px_0_#0284C7]">{user.name?.[0]?.toUpperCase() || "U"}</div>
          <div className="hidden sm:block">
            <div className="font-display font-bold text-slate-800 leading-tight">{user.name}</div>
            <div className="text-xs text-slate-500">Level {user.level}</div>
          </div>
        </Link>
        <div className="flex-1 max-w-xs hidden sm:block">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1 font-semibold">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500"/> {user.xp} XP
            <span className="ml-auto">{xpPct}/{xpToNext}</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden border-2 border-slate-200">
            <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all" style={{width: `${xpPct}%`}} data-testid="xp-bar"/>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full font-bold" data-testid="streak-badge">
            <Flame className="w-4 h-4"/> {user.streak}
          </div>
          <Link to="/child/rewards" className="hidden sm:flex items-center gap-1 bg-rose-100 text-rose-700 px-3 py-1.5 rounded-full font-bold" data-testid="rewards-link">
            <Trophy className="w-4 h-4"/> Shop
          </Link>
          <button onClick={handleLogout} data-testid="logout-btn" className="text-slate-500 hover:text-rose-600 p-2"><LogOut className="w-5 h-5"/></button>
        </div>
      </div>
    </header>
  );
}
